// Карточка задачи как данные: черновик, какие поля отличаются, что записать при сохранении.
// Чистые функции — карточка (TaskModal) только рисует и вызывает.
import { mondayOf, spanWeeks } from '@core/dates';
import type { Change } from '@core/store/model';
import { findFreeTrack, type Assignee } from './planner';
import type { Task } from './data';

export type Draft = {
  id: string | null;
  title: string;
  project_id: string;
  subproject_id: string | null;
  start_date: string;
  end_date: string;
  assignees: string[];
  design_status: 'not_needed' | 'needed' | 'done';
  deploy: 'auto' | 'none' | 'date';
  deploy_date: string;
  notes: string;
  in_backlog: boolean;
  key_release: boolean;                               // звезда: ключевой релиз (внешняя метка)
  release_confidence: 'confirmed' | 'at_risk' | null; // уверенность в дате выхода (внешняя метка)
};

// Поля карточки, которые пишутся вместе: даты — одним куском, проект с подпроектом — одним.
export type Field = 'title' | 'project' | 'dates' | 'assignees' | 'design_status' | 'deploy' | 'release' | 'notes' | 'in_backlog';
export type CardTab = 'task' | 'questions' | 'history';

export function draftFromTask(t: Task, assignees: string[]): Draft {
  return {
    id: t.id, title: t.title, project_id: t.project_id ?? '', subproject_id: t.subproject_id,
    start_date: t.start_date, end_date: t.end_date, assignees,
    design_status: (t.design_status as Draft['design_status']) ?? 'not_needed',
    deploy: t.deploy as Draft['deploy'], deploy_date: t.deploy_date ?? t.end_date, notes: t.notes ?? '',
    in_backlog: t.in_backlog,
    key_release: t.key_release, release_confidence: (t.release_confidence as Draft['release_confidence']) ?? null,
  };
}

/** Новая задача: неделя понедельник–воскресенье вокруг start. */
export function newDraft(project_id: string, subproject_id: string | null, start: string, inBacklog = false): Draft {
  const s = spanWeeks(mondayOf(start), 1);
  return {
    id: null, title: '', project_id, subproject_id, start_date: s.start_date, end_date: s.end_date,
    assignees: [], design_status: 'not_needed', deploy: 'auto', deploy_date: s.end_date, notes: '', in_backlog: inBacklog,
    key_release: false, release_confidence: null,
  };
}

const norm = (d: Draft) => ({ ...d, title: d.title.trim(), notes: d.notes.trim(), subproject_id: d.subproject_id || null });

/**
 * Поля, в которых b отличается от a. Пробелы по краям и пустой подпроект не считаются;
 * дата деплоя — только когда деплой «на дату». Одна функция и для «что изменил другой»,
 * и для «что тронул я»: правило одно, расходиться нечему.
 */
export function diffFields(a: Draft, b: Draft): Field[] {
  const x = norm(a); const y = norm(b);
  const out: Field[] = [];
  if (x.title !== y.title) out.push('title');
  if (x.project_id !== y.project_id || x.subproject_id !== y.subproject_id) out.push('project');
  if (x.start_date !== y.start_date || x.end_date !== y.end_date) out.push('dates');
  if (x.assignees.join() !== y.assignees.join()) out.push('assignees');
  if (x.design_status !== y.design_status) out.push('design_status');
  if (x.deploy !== y.deploy || (y.deploy === 'date' && x.deploy_date !== y.deploy_date)) out.push('deploy');
  if (x.key_release !== y.key_release || x.release_confidence !== y.release_confidence) out.push('release');
  if (x.notes !== y.notes) out.push('notes');
  if (x.in_backlog !== y.in_backlog) out.push('in_backlog');
  return out;
}

/** Новая задача без карточки (быстрая форма): вставка со свободным треком и исполнителями. */
export const planNewTask = (d: Draft, rowTasks: Task[], newId: () => string): { taskId: string; changes: Change[] } => planTaskSave(d, new Set(), undefined, [], rowTasks, newId);

/**
 * Что записать при сохранении карточки. Новая задача — вставка со свободным треком и исполнителями.
 * Существующая — только тронутые и реально изменённые поля (dirty), трек пересчитывается при смене
 * строки или дат, исполнители — разницей. expect_updated_at живой строки: чужая правка между
 * чтением и записью даёт conflict, а не тихую перезапись.
 */
export function planTaskSave(d: Draft, dirty: ReadonlySet<Field>, live: Task | undefined, existing: Assignee[], rowTasks: Task[], newId: () => string): { taskId: string; changes: Change[] } {
  const changes: Change[] = [];
  if (!d.id) {
    const taskId = newId();
    const track = d.in_backlog ? 0 : findFreeTrack(rowTasks, d.start_date, d.end_date);
    changes.push({ table: 'task', insert: {
      id: taskId, track,
      title: d.title.trim(), project_id: d.project_id, subproject_id: d.subproject_id || null,
      start_date: d.start_date, end_date: d.end_date,
      design_status: d.design_status, deploy: d.deploy, deploy_date: d.deploy === 'date' ? d.deploy_date : null,
      notes: d.notes.trim() || null, in_backlog: d.in_backlog,
      key_release: d.key_release, release_confidence: d.release_confidence,
    } });
    d.assignees.forEach((person_id, position) => changes.push({ table: 'task_assignee', insert: { task_id: taskId, person_id, position } }));
    return { taskId, changes };
  }
  const taskId = d.id;
  if (!live) return { taskId, changes };
  const set: Record<string, unknown> = {};
  if (dirty.has('title')) set['title'] = d.title.trim();
  if (dirty.has('project')) { set['project_id'] = d.project_id; set['subproject_id'] = d.subproject_id || null; }
  if (dirty.has('dates')) { set['start_date'] = d.start_date; set['end_date'] = d.end_date; }
  if (dirty.has('design_status')) set['design_status'] = d.design_status;
  if (dirty.has('deploy')) { set['deploy'] = d.deploy; set['deploy_date'] = d.deploy === 'date' ? d.deploy_date : null; }
  if (dirty.has('release')) { set['key_release'] = d.key_release; set['release_confidence'] = d.release_confidence; }
  if (dirty.has('notes')) set['notes'] = d.notes.trim() || null;
  if (dirty.has('in_backlog')) set['in_backlog'] = d.in_backlog;
  if ((dirty.has('project') || dirty.has('dates')) && !d.in_backlog) set['track'] = findFreeTrack(rowTasks, d.start_date, d.end_date, taskId);
  if (Object.keys(set).length > 0) changes.push({ table: 'task', id: taskId, set, expect_updated_at: live.updated_at });
  if (dirty.has('assignees')) {
    const mine = existing.filter((a) => a.task_id === taskId);
    for (const a of mine) if (!d.assignees.includes(a.person_id)) changes.push({ table: 'task_assignee', id: a.id, delete: true });
    d.assignees.forEach((person_id, position) => {
      const was = mine.find((a) => a.person_id === person_id);
      if (!was) changes.push({ table: 'task_assignee', insert: { task_id: taskId, person_id, position } });
      else if (was.position !== position) changes.push({ table: 'task_assignee', id: was.id, set: { position }, expect_updated_at: was.updated_at }); // порядок = полосы на баре
    });
  }
  return { taskId, changes };
}
